/** @jsxImportSource @emotion/react */
import React, { useRef } from "react";
import { Amount } from "../../types/amount";
import NetworkFeeLabel from "./NetworkFeeLabel";
import { SlippageRow } from "./SlippageRow";
import { NetworkFeeRow } from "./NetworkFeeRow";
import { RatesRow } from "./RatesRow";
import { GlobalStore } from "../../store/global/GlobalStore";
import { TotalReceivingRow } from "./TotalReceivingRow";
import { TradeDetailsRow } from "./elements/TradeDetailsRow";
import { AccordionDownIcon } from "./elements/AccordionDownIcon";
import { Spacer } from "../general/Spacer/Spacer";
import AnimatedDropdownContent from "../AnimatedDropdown/AnimatedDropdown";
import { useDisplayOnClick } from "../../hooks/useDisplayOnClick";
import { TradeDetailsButton } from "./elements/TradeDetailsButton";
import { FlexBetweenContainer } from "../../containers/FlexBetweenContainer";
import { css, keyframes } from "@emotion/react";
import { Text } from "../general/Text";
import styled from "@emotion/styled";
import { withIsVisible } from "../../utils/reactUtils";
import LoadingEllipse from "../ButterComponents/LoadingEllipse/LoadingEllipse";

export interface TradeDetailsSectionProps {
  networkFee: Amount;
}
const TradeDetailsSection: React.FC<TradeDetailsSectionProps> = props => {
  const { toggleDisplay, isDisplayed, animationState } = useDisplayOnClick({ duration: 250 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const theme = GlobalStore.useState(s => s.theme);
  const isRequestingQuote = GlobalStore.useState(s => s.isRequestingQuote);

  return (
    <TradeDetailsContainer data-testid={"details-section-container"}>
      <FlexBetweenContainer
        stretch={true}
        css={css`
          height: 26px;
        `}
      >
        <TradeDetailsButton
          ref={buttonRef}
          type="button"
          onClick={toggleDisplay}
          data-testid="trade-details-btn"
        >
          <Text textStyle="Paragraph" size="Large" color={theme.font.tertiary.default}>
            Details
          </Text>
          <Spacer width="3px" />
          <AccordionDownIcon width={16} height={16} isAccordionOpen={isDisplayed} />
        </TradeDetailsButton>
        <NetworkFeeLabel networkFee={props.networkFee} isVisible={!isDisplayed} />
        <LoadingEllipse isVisible={isDisplayed && isRequestingQuote} />
      </FlexBetweenContainer>
      {isDisplayed && (
        <AnimatedDropdownContent
          animationState={animationState}
          duration={250}
          openingAnimation={openingTradeDetailsAnimation}
          closingAnimation={closingTradeDetailsAnimation}
          data-testid="details-section-content"
          childContainerProps={{
            css: css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
              align-self: stretch;
            `,
          }}
        >
          <TradeDetailsRow>
            <NetworkFeeRow />
            <RatesRow />
            <TotalReceivingRow />
            <SlippageRow />
          </TradeDetailsRow>
        </AnimatedDropdownContent>
      )}
    </TradeDetailsContainer>
  );
};

const TradeDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  width: 100%;
`;

// Animation
export const openingTradeDetailsAnimation = keyframes`
  0% {
    max-height: 0;
    opacity: 0;
  }
  100% {
    max-height: 450px;
    opacity: 1;
  }
`;

export const closingTradeDetailsAnimation = keyframes`
  0% {
    max-height: 450px;
    opacity: 1;
  } 
  100% {
    max-height: 0px;
    opacity: 0;
  }
`;

export default withIsVisible(TradeDetailsSection);
